<template>
  <!--
    Sign out. There was none, anywhere in this app: the API has answered
    POST /api/auth/logout since the first login, and nothing in the browser
    ever called it, so leaving an account meant clearing cookies by hand.

    A <button>, not a link: it changes state on the server and it must not be
    something a crawler, a prefetch or a middle click can trigger. It is
    dressed as a nav entry because that is where a person looks for it.

    The session is a cookie only the API can clear (HttpOnly), so the order
    is: ask the API to clear it, THEN forget the CSRF token and leave. If the
    request fails the person is still signed in, and is told so rather than
    being shown a login page that would bounce them straight back in.
  -->
  <div class="lw-p-nav-signout">
    <button
      type="button"
      class="lw-p-nav-signout__button"
      :disabled="busy"
      data-test="sign-out"
      @click="signOut"
    >
      <i class="fa fa-sign-out" aria-hidden="true" />
      <span class="lw-p-nav-signout__name">{{ busy ? 'Signing out' : 'Sign out' }}</span>
    </button>
    <p v-if="failed" class="lw-p-nav-signout__error" role="alert">
      Could not sign out. Check your connection and try again.
    </p>
  </div>
</template>

<script>
import api from '../../../config/api';

export default {
  props: {
    // Where to go afterwards. Injected so a test does not navigate jsdom.
    leave: {
      type: Function,
      default: url => window.location.assign(url),
    },
  },
  data() {
    return { busy: false, failed: false };
  },
  methods: {
    async signOut() {
      if (this.busy) return;
      this.busy = true;
      this.failed = false;
      try {
        await this.$http.post('/auth/logout');
      } catch (err) {
        this.busy = false;
        this.failed = true;
        return;
      }
      try {
        window.sessionStorage.removeItem('csrf_token');
      } catch (err) {
        // Storage can be unavailable (private mode); the cookie is what mattered.
      }
      this.leave(api.login);
    },
  },
};
</script>

<style scoped>
/*
  Dressed exactly as navLink.vue dresses a root nav entry (same padding, type
  role, resting opacity and hover), so it reads as the last entry of the rail.
  The rules are repeated rather than shared because navLink's are scoped to
  navLink; see that file for why each value is what it is.
*/
.lw-p-nav-signout {
  border-top: 1px solid var(--lw-navy-800);
  margin-top: var(--lw-space-4);
  padding-top: var(--lw-space-2);
}

.lw-p-nav-signout__button {
  appearance: none;
  background: none;
  border: 0;
  border-left: 3px solid transparent;
  color: var(--lw-fg-inverse);
  cursor: pointer;
  display: block;
  font: var(--lw-type-label);
  letter-spacing: var(--lw-type-label-tracking);
  opacity: 0.62;
  padding: var(--lw-space-2) var(--lw-space-6);
  text-align: left;
  text-transform: var(--lw-type-label-transform);
  transition: opacity var(--lw-motion-fast) var(--lw-motion-ease);
  width: 100%;
}

.lw-p-nav-signout__button:hover,
.lw-p-nav-signout__button:focus-visible {
  background-color: var(--lw-navy-950);
  opacity: 1;
}

.lw-p-nav-signout__button:disabled {
  cursor: default;
}

.lw-p-nav-signout__error {
  color: var(--lw-fg-inverse);
  font: var(--lw-type-body-sm);
  margin: 0;
  padding: var(--lw-space-2) var(--lw-space-6);
}

#sidebar.collapsed .lw-p-nav-signout__button {
  text-align: center;
}

#sidebar.collapsed .lw-p-nav-signout__name,
#sidebar.collapsed .lw-p-nav-signout__error {
  display: none;
}

#sidebar.expanded .lw-p-nav-signout__button .fa {
  padding-right: 0.3em;
}
</style>
